import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import Calendar from '~/components/schedules/calendar-v2/Calendar';
import { Views, ViewsProps } from 'react-big-calendar';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { scheduleActions } from '~/thunks/schedule/scheduleSlice';
import { handleGetEvents } from '~/thunks/schedule/scheduleThunk';
import AddAppointmentModal from '~/components/schedules/add-appointment-modal/AddAppointmentModal';
import { IScheduleEvent } from '~/utils/interface/schedule';
import { catchError, notify } from '~/utils/helper';
import scheduleEventApi from '~/apis/schedule';
import PrimaryButton from '~/components/common/primary-button/PrimaryButton';
import { PlusIcon } from 'lucide-react';
import styles from './CalendarTab.module.scss';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

dayjs.extend(isSameOrBefore);
dayjs.extend(advancedFormat);

const views: ViewsProps = {
  month: true,
  week: true,
  day: true,
};

interface CalendarTabProps {
  jobId: string;
}

const CalendarTab = ({ jobId }: CalendarTabProps) => {
  const [calendarViewMode, setCalendarViewMode] = useState<'month' | 'week' | 'day'>('month');
  const [currentTime, setCurrentTime] = useState(dayjs().format('YYYY-MM-DD'));
  const [openAddAppointmentModal, setOpenAddAppointmentModal] = useState(false);
  const [appointmentModalMode, setAppointmentModalMode] = useState<'add' | 'view' | 'edit'>('add');
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | undefined>(undefined);

  const { needRefreshData, events: reduxEvents } = useAppSelector((state) => state.schedule);
  const dispatch = useAppDispatch();

  // Fetch events filtered by jobId
  useEffect(() => {
    if (!jobId) return;

    const from = dayjs(currentTime).startOf(calendarViewMode).format('YYYY-MM-DD');
    const to = dayjs(currentTime).endOf(calendarViewMode).format('YYYY-MM-DD');

    dispatch(
      handleGetEvents({
        from,
        to,
        relatedJobId: parseInt(jobId),
      })
    );
  }, [dispatch, currentTime, calendarViewMode, jobId]);

  // Refresh data when needed
  useEffect(() => {
    if (!jobId || !needRefreshData) return;

    const from = dayjs(currentTime).startOf(calendarViewMode).format('YYYY-MM-DD');
    const to = dayjs(currentTime).endOf(calendarViewMode).format('YYYY-MM-DD');

    dispatch(
      handleGetEvents({
        from,
        to,
        relatedJobId: parseInt(jobId),
      })
    );
  }, [dispatch, currentTime, calendarViewMode, needRefreshData, jobId]);

  // Process events from Redux and split multi-day schedules
  const events = useMemo(() => {
    if (reduxEvents.length === 0) return [];

    const splitSchedules: IScheduleEvent[] = [];

    reduxEvents.forEach((schedule) => {
      const startDate = dayjs(schedule.startAt);
      const endDate = dayjs(schedule.endAt);

      if (!startDate.isSame(endDate, 'day')) {
        let currentDate = startDate;
        let dayIndex = 0;

        while (currentDate.isSameOrBefore(endDate, 'day')) {
          const isFirstDay = dayIndex === 0;
          const isLastDay = currentDate.isSame(endDate, 'day');

          let dayStartTime, dayEndTime;

          if (schedule.allDayChecked) {
            dayStartTime = currentDate.startOf('day').toDate();
            dayEndTime = currentDate.endOf('day').toDate();
          } else {
            if (isFirstDay) {
              dayStartTime = startDate.toDate();
              dayEndTime = currentDate.endOf('day').toDate();
            } else if (isLastDay) {
              dayStartTime = currentDate.startOf('day').toDate();
              dayEndTime = endDate.toDate();
            } else {
              dayStartTime = currentDate.startOf('day').toDate();
              dayEndTime = currentDate.endOf('day').toDate();
            }
          }

          splitSchedules.push({
            id: `${schedule.scheduleId}_${currentDate.format('YYYY-MM-DD')}_${dayIndex}`,
            name: schedule.appointmentTitle,
            description: schedule.description || '',
            priority: schedule.priority?.toLowerCase() || 'medium',
            startAt: dayStartTime,
            endAt: dayEndTime,
            startTime: dayjs(dayStartTime).format('HH:mm:ss+07:00'),
            endTime: dayjs(dayEndTime).format('HH:mm:ss+07:00'),
            assigneeColor: schedule.assigneeColor || '#3b82f6',
            assignees: schedule.assignee ? [{
              id: schedule.assigneeId,
              name: schedule.assignee,
              avatar: '',
            }] : null,
            appointmentType: schedule.appointmentType,
            isRecurring: schedule.isRecurring,
            relatedJob: schedule.relatedJob || undefined,
            subcontractor: schedule.subcontractor || undefined,
            subcontractorColor: schedule.subcontractorColor || undefined,
            originalScheduleId: schedule.scheduleId,
            occurrenceId: schedule.occurrenceId,
          });

          currentDate = currentDate.add(1, 'day');
          dayIndex++;
        }
      } else {
        splitSchedules.push({
          id: schedule.scheduleId,
          name: schedule.appointmentTitle,
          description: schedule.description || '',
          priority: schedule.priority?.toLowerCase() || 'medium',
          startAt: new Date(schedule.startAt),
          endAt: new Date(schedule.endAt),
          startTime: dayjs(schedule.startAt).format('HH:mm:ss+07:00'),
          endTime: dayjs(schedule.endAt).format('HH:mm:ss+07:00'),
          assigneeColor: schedule.assigneeColor || '#3b82f6',
          assignees: schedule.assignee ? [{
            id: schedule.assigneeId,
            name: schedule.assignee,
            avatar: '',
          }] : null,
          appointmentType: schedule.appointmentType,
          isRecurring: schedule.isRecurring,
          relatedJob: schedule.relatedJob || undefined,
          subcontractor: schedule.subcontractor || undefined,
          subcontractorColor: schedule.subcontractorColor || undefined,
          originalScheduleId: schedule.scheduleId,
          occurrenceId: schedule.occurrenceId,
        });
      }
    });

    return splitSchedules;
  }, [reduxEvents]);

  const onSelectEvent = useCallback((event: IScheduleEvent) => {
    setAppointmentModalMode('edit');
    setSelectedScheduleId(parseInt(String(event.originalScheduleId || event.id), 10));
    setOpenAddAppointmentModal(true);
  }, []);

  const handleAddAppointment = () => {
    setAppointmentModalMode('add');
    setSelectedScheduleId(undefined);
    setOpenAddAppointmentModal(true);
  };

  const handleDeleteSchedule = async () => {
    if (!selectedScheduleId) return;

    try {
      await scheduleEventApi.deleteSchedule(selectedScheduleId);
      notify('success', 'Appointment deleted successfully');
      setOpenAddAppointmentModal(false);
      setAppointmentModalMode('add');
      setSelectedScheduleId(undefined);
      dispatch(scheduleActions.changeNeedRefreshData(true));
    } catch (error) {
      catchError(error, (message) => {
        notify('error', message);
      });
    }
  };

  const handleEditSchedule = () => {
    setAppointmentModalMode('edit');
  };

  return (
    <Box className={cx('calendar-tab-container')}>
      <Box className={cx('calendar-header')}>
        <PrimaryButton
          title="Add Appointment"
          icon={<PlusIcon size={16} />}
          onClick={handleAddAppointment}
          className={cx('add-button')}
        />
      </Box>

      <Box className={cx('calendar-content')}>
        <Calendar
          view={calendarViewMode}
          date={currentTime}
          events={events}
          onSelectEvent={onSelectEvent}
        />
      </Box>

      <AddAppointmentModal
        open={openAddAppointmentModal}
        mode={appointmentModalMode}
        scheduleId={selectedScheduleId}
        onDelete={handleDeleteSchedule}
        onEdit={handleEditSchedule}
        defaultRelatedJobId={jobId}
        onClose={() => {
          setOpenAddAppointmentModal(false);
          setAppointmentModalMode('add');
          setSelectedScheduleId(undefined);
          dispatch(scheduleActions.changeNeedRefreshData(true));
        }}
      />
    </Box>
  );
};

export default CalendarTab;
